import {defineStore} from 'pinia'
import type {DecathlonStore} from "~/types/decathlonStore";
import type {Country} from "~/types/countries";
import type {CrossDiag, CrossDiagGroup} from "~/types/diagnostic";
import type {AdminStatus, CountryScope} from "~/types/admin/theme";
import type {Language} from "~/types/language";

const CURRENT_VERSION = "1.0.2";
export const useDecathlonStores = defineStore('decathlonStores', () => {
    const runtimeConfig = useRuntimeConfig()
    const userStore = useUserStore()
    const languageStore = useLanguageStore()


    const version = ref() // Version persistée
    const selectedCountry = ref<Country>()
    const selectedStore = ref<DecathlonStore>()
    const countries = ref<Country[] | undefined>([])
    const crossDiagGroups = ref<CrossDiagGroup[] | undefined>([])
    const selectedCrossDiag = ref<CrossDiag>()


    const setSelectedCountry = (country: Country) => {
        selectedCountry.value = country
    }
    const setSelectedStore = (store: DecathlonStore) => {
        selectedStore.value = store
    }
    const setSelectedCrossDiag = (diag: CrossDiag | undefined) => {
        selectedCrossDiag.value = diag
        if (diag)
            languageStore.currentLanguage = diag.languages?.find(lang => lang.id === languageStore.currentLanguage?.id) ?? diag.languages?.[0]
    }

    const setCrossDiagGroups = (fetchedCrossDiagGroups: CrossDiagGroup[]) => {
        crossDiagGroups.value = fetchedCrossDiagGroups
    }

    const fetchCountries = async () => {
        countries.value = await useFetchWithToken<Country[]>('fetchCountries', `${runtimeConfig.public.apiBase}/v1/countries`, {
            method: 'get'
        })
        return countries.value
    }
    const fetchDecathlonStores = async (currentCountry: Country) =>
        await useFetchWithToken<DecathlonStore[]>('fetchDecathlonStores', `${runtimeConfig.public.apiBase}/v1/stores?countryId=${currentCountry.id}`, {
            method: 'get'
        })

    const fetchStoreTypes = async () =>
        await useFetchWithToken<string[]>('fetchDecathlonStores', `${runtimeConfig.public.apiBase}/v1/stores/types`, {
            method: 'get'
        })

    const fetchStoreCategories = async () =>
        await useFetchWithToken<string[]>('fetchDecathlonStores', `${runtimeConfig.public.apiBase}/v1/stores/categories`, {
            method: 'get'
        })

    const fetchCrossDiagGroups = async (store?: DecathlonStore) => {
        if (!store && !selectedStore.value) return

        const url = `${runtimeConfig.public.apiBase}/v1/${userStore.isAdmin ? 'admin/' : ''}diagnostic-groups?storeId=${store ? store.id : selectedStore?.value?.id}`
        const crossDiagGroups = await useFetchWithToken<CrossDiagGroup[]>('fetchCrossDiagGroups', url, {
            method: 'get'
        }) ?? []
        if (!store)
            setCrossDiagGroups(crossDiagGroups)
        return crossDiagGroups
    }

    const fetchSelectedStore = async (store: DecathlonStore | undefined) =>
        selectedStore.value = await fetchStoreById(store?.id)

    const fetchStoreById = async (storeId: number | undefined) => await useFetchWithToken<DecathlonStore>('fetchDecathlonStores', `${runtimeConfig.public.apiBase}/v1/stores/${storeId}`, {
        method: 'get'
    })

    const fetchCrossDiagById = async (crossDiagId: number) => useFetchWithToken<CrossDiag>('fetchSelectedCrossDiag',
        `${runtimeConfig.public.apiBase}/v1/${userStore.isAdmin ? 'admin/' : ''}diagnostics/${crossDiagId}?storeId=${selectedStore?.value?.id}`, {
            method: 'get',
        })


    const fetchSelectedCrossDiag = async () => {
        if (selectedCrossDiag.value)
            selectedCrossDiag.value = await fetchCrossDiagById(selectedCrossDiag.value?.id)
    }

    const updateCrossDiagLabel = async (label: string, language: string) => {
        selectedCrossDiag.value = await useFetchWithToken('updateCrossDiagLabel',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/label`, {
                method: 'patch',
                body: {
                    text: label,
                    language: language
                }
            })
    }

    const updateCrossDiagDescription = async (description: string, language: string) => {
        selectedCrossDiag.value = await useFetchWithToken('updateCrossDiagDescription',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/description`, {
                method: 'patch',
                body: {
                    text: description,
                    language: language
                }
            })
    }

    const updateCountryScope = async (countryScope: CountryScope) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('updateCountryScope',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/country-scope`, {
                method: 'patch',
                body: JSON.stringify(countryScope)
            })
    }

    const updateCountries = async (countries: Country[]) => {
        const ids = countries.map((country) => country.id)
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('updateCountry',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/update-specific-country`, {
                method: 'patch',
                body: ids
            })
    }

    const updateCrossDiagStatus = async (crossDiagId: string[], status: AdminStatus) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('updateCrossDiagStatus',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${crossDiagId[0]}/${status.toLowerCase()}`, {
                method: 'patch'
            })
    }

    const addLanguage = async (language: Language) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('add-language',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/add-language/${language.id}`, {
                method: 'patch',
            })
    }

    const removeLanguage = async (languageId: string) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('remove-language',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/remove-language/${languageId}`, {
                method: 'patch',
            })
    }

    const updateLanguagePosition = async (language: Language, position: number) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('update-language-position',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${selectedCrossDiag.value?.id}/languages/${language.id}/position/${position}`, {
                method: 'patch',
            })
    }

    const createCrossDiag = async (label: string) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('createCrossDiag',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics?storeId=${selectedStore.value?.id}`, {
                method: 'post',
                body: {
                    label: label,
                    description: 'description...',
                    language: languageStore.currentLanguage?.id,
                    countryScope: 'LOCAL',
                    countryCodes: []
                }
            })
    }

    const importCrossDiag = async (spreadsheetId: string) => {
        selectedCrossDiag.value = await useFetchWithToken<CrossDiag>('importCrossDiag',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/import?spreadsheetId=${encodeURIComponent(spreadsheetId)}`, {
                method: 'post',
            })
    }

    const crossDiagHasResponses = async (diagnosticId: number) => {
        return await useFetchWithToken<boolean>('crossDiagHasResponses',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${diagnosticId}/has-responses`, {
                method: 'get',
            })
    }

    // Ne vide jamais selectedCrossDiag ici de manière synchrone : cette fonction peut être
    // appelée depuis une page qui ne navigue pas immédiatement (ex: ChooseDiag.vue), pendant
    // laquelle des watchers (pages report/*, pages/store) restent montés et refetch dès que
    // selectedCrossDiag change, y compris vers undefined. On retourne donc l'information au lieu
    // d'agir, pour laisser l'appelant décider ORDRE (naviguer avant de vider, ou vider seulement
    // s'il reste sur une page qui n'observe pas selectedCrossDiag).
    const purgeCrossDiag = async (diagnosticId: number): Promise<boolean> => {
        await useFetchWithToken<void>('purgeCrossDiag',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${diagnosticId}/purge`, {
                method: 'delete',
            })
        const wasSelected = selectedCrossDiag.value?.id === diagnosticId
        await fetchCrossDiagGroups()
        return wasSelected
    }

    const updateGroupAndPosition = async (diagnosticId: number, groupId: number, position: number) => {
        await useFetchWithToken<CrossDiag>('updateGroupAndPosition',
            `${runtimeConfig.public.apiBase}/v1/admin/diagnostics/${diagnosticId}/group-and-position`, {
                method: 'patch',
                body: {groupId, position}
            })
        await fetchCrossDiagGroups()
    }

    const selectedCrossDiagExist = () => crossDiagGroups.value?.flatMap(g => g.crossDiags).find(cd => cd.id === selectedCrossDiag.value?.id)

    const checkAndMigrateVersion = () => {
        const persistedVersion = version.value;

        if (persistedVersion !== CURRENT_VERSION) {
            if (selectedStore.value)
                fetchSelectedStore(selectedStore.value)
            // Update version to the current version
            version.value = CURRENT_VERSION
        }
    }

    const downloadPhotosZip = async (): Promise<boolean> => {
        if (!selectedCrossDiag.value?.id || !selectedStore.value?.id) return false

        const diagnosticId = selectedCrossDiag.value.id.toString()
        const storeId = selectedStore.value.id.toString()

        try {
            // 1. Appel léger pour obtenir le jeton de téléchargement éphémère
            const tokenResponse = await useFetchWithToken<{ token: string }>(
                'downloadPhotosZipToken',
                `${runtimeConfig.public.apiBase}/v1/diagnostic/store/photos/download-token?diagnosticId=${diagnosticId}&storeId=${storeId}`,
                {
                    method: 'GET'
                }
            )

            if (!tokenResponse?.token) {
                console.error("Impossible de récupérer le jeton de téléchargement")
                return false
            }

            // 2. On déclenche le téléchargement natif du navigateur
            const downloadUrl = `${runtimeConfig.public.apiBase}/v1/diagnostic/store/photos/download?token=${tokenResponse.token}`

            // Le navigateur prend le relais sans bloquer la mémoire de l'application
            window.location.href = downloadUrl

            return true

        } catch (error) {
            console.error("Erreur lors de l'initialisation du téléchargement :", error)
            return false
        }
    }

    const isMyEfficiency = () => selectedCrossDiag.value?.id === 1

    const isIdentityDiagnostic = () => selectedCrossDiag.value?.id == 3 || selectedCrossDiag.value?.id == 2

    return {
        version,
        selectedStore,
        countries,
        selectedCountry,
        selectedCrossDiag,
        crossDiagGroups,
        selectedCrossDiagExist,
        isMyEfficiency,
        isIdentityDiagnostic,
        fetchDecathlonStores,
        fetchStoreById,
        fetchSelectedStore,
        fetchCrossDiagById,
        fetchSelectedCrossDiag,
        fetchCountries,
        setSelectedCountry,
        setSelectedStore,
        setSelectedCrossDiag,
        updateCrossDiagLabel,
        updateCrossDiagDescription,
        updateCountryScope,
        updateCountries,
        updateCrossDiagStatus,
        addLanguage,
        removeLanguage,
        updateLanguagePosition,
        createCrossDiag,
        importCrossDiag,
        purgeCrossDiag,
        crossDiagHasResponses,
        updateGroupAndPosition,
        setCrossDiagGroups,
        fetchCrossDiagGroups,
        fetchStoreTypes,
        fetchStoreCategories,
        checkAndMigrateVersion,
        downloadPhotosZip
    }
}, {
    persist: {
        pick: ['version', 'selectedStore', 'selectedCountry', 'selectedCrossDiag'],
        afterHydrate: (context) => {
            console.log("afterHydrate decathlonStores")
            context.store.checkAndMigrateVersion()
        }
    }
})
